import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "chachophone_cart";

function read() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

export default function CartProvider({ children }) {
  const [items, setItems] = useState(read);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  // Keep the cart in sync if the shopper has two tabs open.
  useEffect(() => {
    const sync = (e) => {
      if (e.key === KEY) setItems(read());
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const add = (product, quantity = 1) => {
    setItems((current) => {
      const found = current.find((item) => item.sku === product.sku);
      if (found) {
        return current.map((item) =>
          item.sku === product.sku ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [
        ...current,
        {
          // Fields the existing order endpoint reads — don't rename these.
          _id: product.sku,
          title: [product.brand, product.title].filter(Boolean).join(" "),
          price: product.price,
          images: [product.image],
          // Fields ChachoPhone's own UI reads.
          sku: product.sku,
          brand: product.brand,
          name: product.title,
          image: product.image,
          specs: product.specs || {},
          quantity,
        },
      ];
    });
  };

  const setQuantity = (sku, quantity) =>
    setItems((current) =>
      current.map((item) =>
        item.sku === sku ? { ...item, quantity: Math.max(1, Math.min(10, quantity)) } : item
      )
    );

  const remove = (sku) => setItems((current) => current.filter((item) => item.sku !== sku));

  const clear = () => setItems([]);

  const value = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + (Number(item.price) || 0) * item.quantity, 0);
    const count = items.reduce((sum, item) => sum + item.quantity, 0);

    return {
      items,
      count,
      subtotal,
      total: subtotal, // shipping is free — see Checkout
      add,
      setQuantity,
      remove,
      clear,
      has: (sku) => items.some((item) => item.sku === sku),
      // Shape the order endpoint expects. The backend re-reads price/stock by
      // sku itself — title/price/images here are just for the order summary UI.
      toOrderPayload: () =>
        items.map((item) => ({
          sku: item.sku,
          _id: item._id,
          title: item.title,
          price: item.price,
          quantity: item.quantity,
          images: item.images,
        })),
    };
  }, [items]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
